import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import type { LumberRail } from "building-types/framing/lumberTypes";
import { WallFramingStartCorner, WallFramingType } from "building-types/framing/wallFramingTypes";
import { makeFramingProfile2D } from "doc/utils/lumberTools";
import type { Face } from "geom/brep";
import { clipFacesWithPlanes } from "geom/bspClipping";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { line2Normal } from "geom/line2";
import {
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2Move,
  polyLine2Vertex,
  polyLine2VertexCount,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3Copy,
  polyLine3EdgeTangent,
  polyLine3ExtendBothEnds,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3Move,
} from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import { computeProjectedDistance, framingDirectionVector, framingPerpendicularVector } from "../framingLayoutUtils";

/**
 * Perimeter corner for stud layout origin in the framing (parallel, perpendicular) frame.
 * Bottom/top use min/max parallel distance; left/right use max/min perpendicular distance.
 */
export function wallFramingStartPtForCorner(
  perimeter: PolyLine2,
  framingDirection: Vec2,
  perpDirection: Vec2,
  corner: WallFramingStartCorner,
): Vec2 {
  const planeOrigin: Vec2 = [0, 0];
  const preferMinParallel =
    corner === WallFramingStartCorner.BOTTOM_LEFT || corner === WallFramingStartCorner.BOTTOM_RIGHT;
  const preferMaxPerp = corner === WallFramingStartCorner.BOTTOM_LEFT || corner === WallFramingStartCorner.TOP_LEFT;

  let bestParallel = preferMinParallel ? Infinity : -Infinity;
  let bestPerp = preferMaxPerp ? -Infinity : Infinity;
  let startPt: Vec2 = [0, 0];

  for (let i = 0, l = polyLine2VertexCount(perimeter) - 1; i < l; i++) {
    const [x, y] = polyLine2Vertex(perimeter, i);
    const vertex: Vec2 = [x, y];
    const parallel = computeProjectedDistance(vertex, planeOrigin, framingDirection);
    const perp = computeProjectedDistance(vertex, planeOrigin, perpDirection);

    const parallelIsBetter = preferMinParallel
      ? parallel < bestParallel - DIST_EPSILON
      : parallel > bestParallel + DIST_EPSILON;
    const parallelIsTie = Math.abs(parallel - bestParallel) <= DIST_EPSILON;
    const perpIsBetter = preferMaxPerp ? perp > bestPerp + DIST_EPSILON : perp < bestPerp - DIST_EPSILON;

    if (parallelIsBetter || (parallelIsTie && perpIsBetter)) {
      bestParallel = parallel;
      bestPerp = perp;
      startPt = vertex;
    }
  }

  return startPt;
}

/** Bottom-left corner; prefer {@link wallFramingStartPtForCorner} for other corners. */
export function defaultWallFramingStartPt(perimeter: PolyLine2, framingDirection: Vec2, perpDirection: Vec2): Vec2 {
  return wallFramingStartPtForCorner(perimeter, framingDirection, perpDirection, WallFramingStartCorner.BOTTOM_LEFT);
}

/**
 * Perimeter corner for stud layout; pass as `framingMetadata.framingStartPt`.
 * V2 stud spacing is one-sided from this point ({@link generateFramingSpacingOriginPoints}); use a
 * wall-end corner, not a mid-run point, unless product explicitly accepts half-span OC.
 */
export function computeWallFramingStartPt(
  perimeter: PolyLine2,
  directionRadians: number = Math.PI / 2,
  corner: WallFramingStartCorner = WallFramingStartCorner.BOTTOM_LEFT,
): Vec2 {
  const framingDirection = framingDirectionVector(directionRadians);
  const perpDirection = framingPerpendicularVector(directionRadians);
  return wallFramingStartPtForCorner(perimeter, framingDirection, perpDirection, corner);
}

export function isPlateFramingType(type: WallFramingType): boolean {
  return (
    type === WallFramingType.BOTTOM_PLATE ||
    type === WallFramingType.TOP_PLATE ||
    type === WallFramingType.BOTTOM_SILL_PLATE ||
    type === WallFramingType.TOP_SILL_PLATE ||
    type === WallFramingType.BLOCKING
  );
}

/**
 * Lumber depth into the wall (normal to the wall plane, +Y).
 * Plates are flat: profile.height (e.g. 1.5" for a 2x4).
 * Studs are on edge: profile.width (e.g. 3.5" for a 2x4).
 */
export function profileDepthIntoWall(type: WallFramingType, profileDescriptor: FramingProfileDescriptor): number {
  return isPlateFramingType(type) ? profileDescriptor.height : profileDescriptor.width;
}

/**
 * In-plane stack step for multi-count boundary members (mirrors floor rim stack).
 * Studs stack by profile.height.
 */
export function boundaryInPlaneStackStep(_type: WallFramingType, profileDescriptor: FramingProfileDescriptor): number {
  return profileDescriptor.height;
}

/**
 * Stacked plate offset uses extruded in-plane thickness (profile.height for horizontal rails).
 */
export function boundaryPlateStackStep(profileDescriptor: FramingProfileDescriptor): number {
  return profileDescriptor.height;
}

/**
 * Extrusion normal on the XZ wall plane — same rule as floor framing mapped from XY:
 * floor uses (dy, -dx, 0) for tangent (dx, dy); wall uses (dz, 0, -dx) for tangent (dx, dz).
 */
export function wallFramingExtrusionNormal(rail3D: PolyLine3): { nX: number; nY: number; nZ: number } {
  const [tx, , tz] = polyLine3EdgeTangent(rail3D, 0);
  return { nX: tz, nY: 0, nZ: -tx };
}

/**
 * Smaller layout-X edge of the rough opening (legacy frameWall `start`).
 * `OpeningLayoutBounds.startPerp`/`endPerp` follow min/max projected perp, which is inverted
 * relative to layout X when the framing perp axis points toward -X.
 */
export function roughOpeningStartPerp(bounds: { readonly startPerp: number; readonly endPerp: number }): number {
  return bounds.endPerp;
}

/**
 * Larger layout-X edge of the rough opening (legacy frameWall `end`).
 */
export function roughOpeningEndPerp(bounds: { readonly startPerp: number; readonly endPerp: number }): number {
  return bounds.startPerp;
}

/**
 * Header horizontal span between jack-stud outside faces (matches legacy frameWall addHeader).
 */
export function openingHeaderPerpSpan(
  bounds: { readonly startPerp: number; readonly endPerp: number },
  jackOffset: number,
): { readonly startPerp: number; readonly endPerp: number } {
  const roStart = roughOpeningStartPerp(bounds);
  const roEnd = roughOpeningEndPerp(bounds);
  return {
    startPerp: roStart + jackOffset,
    endPerp: roEnd - jackOffset,
  };
}

/**
 * Sill horizontal span across the rough opening (matches legacy frameWall addSill start/end).
 */
export function openingSillPerpSpan(bounds: { readonly startPerp: number; readonly endPerp: number }): {
  readonly startPerp: number;
  readonly endPerp: number;
} {
  return {
    startPerp: roughOpeningEndPerp(bounds),
    endPerp: roughOpeningStartPerp(bounds),
  };
}

/** In-wall stack offset for headers: front face on the framing plane, each layer stepped by header thickness. */
export function openingHeaderPlanDepthOffset(headerThickness: number, stackIndex: number): number {
  return headerThickness * stackIndex;
}

/** On-edge header extrusion (depth into wall is +Y, matching legacy addHeader normal for axis-aligned walls). */
export function openingHeaderExtrusionNormal(): { readonly nX: number; readonly nY: number; readonly nZ: number } {
  return { nX: 0, nY: 1, nZ: 0 };
}

/** Vertical extent above the opening top occupied by headers (on-edge width only; stacked headers grow in wall depth). */
export function openingHeaderVerticalExtent(headerCount: number, headerProfile: FramingProfileDescriptor): number {
  if (headerCount <= 0) {
    return 0;
  }
  return headerProfile.width;
}

/** Flat plate thickness in the wall-height direction (e.g. 1.5" for a flat 2x4). */
export function plateFlatVerticalThickness(profileDescriptor: FramingProfileDescriptor): number {
  return profileDescriptor.height;
}

/** Vertical stack of header sills above the opening top. */
export function openingHeaderSillVerticalExtent(
  headerSillCount: number,
  headerSillProfile: FramingProfileDescriptor,
): number {
  if (headerSillCount <= 0) {
    return 0;
  }
  return headerSillCount * plateFlatVerticalThickness(headerSillProfile);
}

/**
 * Rail parallel for header-sill layer `layerIndex`. Uses the same plate extrusion as window sills
 * ({@link wallFramingExtrusionNormal}, growth toward -Z); rail sits above the layer so the plate
 * bottom is flush with the opening top for layer 0 and stacks upward for additional layers.
 */
export function openingHeaderSillRailParallel(
  openingTopParallel: number,
  layerIndex: number,
  flatThickness: number,
): number {
  return openingTopParallel + (layerIndex + 1) * flatThickness;
}

/**
 * Plate extrusion up vector on the XZ wall plane. Top plates use -Z so profile width runs
 * inward along height; bottom plates use +Z for the same inward growth from the floor edge.
 * @deprecated Plates use {@link wallFramingExtrusionNormal} so depth aligns with studs in +Y.
 */
export function wallFramingPlateExtrusionNormal(framingType: WallFramingType): { nX: number; nY: number; nZ: number } {
  if (framingType === WallFramingType.TOP_PLATE) {
    return { nX: 0, nY: 0, nZ: -1 };
  }
  return { nX: 0, nY: 0, nZ: 1 };
}

/**
 * Stack multi-count boundary members inward from the perimeter edge (mirrors floor rim joists).
 * The first member (stackIndex 0) stays on the input edge; exterior-face alignment is applied in 3D.
 */
export function offsetBoundaryRail2DForStack(
  edgeRail: PolyLine2,
  framingType: WallFramingType,
  profileDescriptor: FramingProfileDescriptor,
  stackIndex: number,
): PolyLine2 {
  if (stackIndex === 0) {
    return polyLine2Copy(edgeRail);
  }
  const rail2D = polyLine2Copy(edgeRail);
  const [nx, ny] = line2Normal(edgeRail[0], edgeRail[1], edgeRail[2], edgeRail[3], true);
  const stackStep = isPlateFramingType(framingType)
    ? boundaryPlateStackStep(profileDescriptor)
    : boundaryInPlaneStackStep(framingType, profileDescriptor);
  // stackIndex 0 stays on the edge (3D offset aligns exterior face). Each inner layer stacks
  // at (stackIndex + 1) steps inward so it meets the plate outside it.
  const inwardSteps = stackIndex + 1;
  polyLine2Move(rail2D, nx * inwardSteps * stackStep, ny * inwardSteps * stackStep);
  return rail2D;
}

/**
 * In-plane extent from the rail toward the exterior when the rail sits on the perimeter edge.
 * Plates on horizontal rails: profile.height (extruded in-plane thickness). Studs: profile.height on edge.
 */
export function boundaryInPlaneExteriorExtent(
  _type: WallFramingType,
  profileDescriptor: FramingProfileDescriptor,
): number {
  return profileDescriptor.height;
}

/**
 * CCW interior normal on the wall plane (XZ) for a 3D edge tangent.
 */
export function boundaryInteriorNormal3D(rail3D: PolyLine3): { ix: number; iz: number } {
  const [tx, , tz] = polyLine3EdgeTangent(rail3D, 0);
  const len = Math.hypot(tx, tz);
  if (len < DIST_EPSILON) {
    return { ix: 0, iz: 0 };
  }
  return { ix: -tz / len, iz: tx / len };
}

/**
 * Offset a perimeter boundary rail so the exterior face sits on the input edge.
 * Stacked members (stackIndex > 0) are positioned by {@link offsetBoundaryRail2DForStack} only.
 */
export function offsetBoundaryRail3DForExteriorFace(
  rail3D: PolyLine3,
  _framingType: WallFramingType,
  profileDescriptor: FramingProfileDescriptor,
  stackIndex: number,
): PolyLine3 {
  if (stackIndex > 0) {
    return rail3D;
  }
  const inPlaneExtent = boundaryInPlaneExteriorExtent(_framingType, profileDescriptor);
  const { nX, nZ } = wallFramingExtrusionNormal(rail3D);
  return polyLine3Move(rail3D, -nX * inPlaneExtent, 0, -nZ * inPlaneExtent);
}

/** Perimeter edge indices incident on a vertex at (x, y). */
export function edgeIndicesAtVertex(perimeter: PolyLine2, x: number, y: number): number[] {
  const edgeCount = polyLine2EdgeCount(perimeter);
  const indices: number[] = [];
  for (let i = 0; i < edgeCount; i++) {
    const edge = polyLine2Edge(perimeter, i);
    if (
      (Math.abs(edge[0] - x) <= DIST_EPSILON && Math.abs(edge[1] - y) <= DIST_EPSILON) ||
      (Math.abs(edge[2] - x) <= DIST_EPSILON && Math.abs(edge[3] - y) <= DIST_EPSILON)
    ) {
      indices.push(i);
    }
  }
  return indices;
}

/**
 * Classify a CCW perimeter edge on a vertical wall polygon.
 * Vertical edges → end studs (STANDARD); horizontal or sloped edges near min Y → bottom plate,
 * near max Y → top plate (supports gable balloon framing with sloped top plates).
 */
export function classifyBoundaryEdgeType(perimeter: PolyLine2, edgeIndex: number): WallFramingType {
  const edge = polyLine2Edge(perimeter, edgeIndex);
  const dx = edge[2] - edge[0];
  const absDx = Math.abs(dx);

  if (absDx < DIST_EPSILON) {
    return WallFramingType.STANDARD_STUD;
  }

  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0, l = polyLine2VertexCount(perimeter) - 1; i < l; i++) {
    const [, y] = polyLine2Vertex(perimeter, i);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  const midY = (edge[1] + edge[3]) / 2;
  const distToMin = Math.abs(midY - minY);
  const distToMax = Math.abs(midY - maxY);
  return distToMin <= distToMax ? WallFramingType.BOTTOM_PLATE : WallFramingType.TOP_PLATE;
}

/**
 * Convert wall framing members to 3D faces for rendering.
 */
export function wallFramingFaces(lumberRail: LumberRail, profileDescriptor: FramingProfileDescriptor): Face[] {
  const profile = [makeFramingProfile2D(profileDescriptor)];

  const { rail, nX, nY, nZ, clipPlanes, startExtension, endExtension } = lumberRail;
  let railExt = polyLine3Copy(rail);
  if (startExtension && endExtension) {
    railExt = polyLine3ExtendBothEnds(railExt, startExtension, endExtension);
  } else if (startExtension) {
    railExt = polyLine3ExtendStart(railExt, startExtension);
  } else if (endExtension) {
    railExt = polyLine3ExtendEnd(railExt, endExtension);
  }
  let faces = extrudeFaceAlongPolyLine3(profile, railExt, nX, nY, nZ).faces;

  if (clipPlanes && clipPlanes.length > 0) {
    faces = clipFacesWithPlanes({
      faces,
      planes: [...clipPlanes],
      addCapFaces: true,
    });
  }

  return faces;
}
